import React, { useState } from 'react';
import { Redirect } from 'react-router-dom';
import { TableRow } from '@material-ui/core';
import Swal from 'sweetalert2';
import * as Utils from '../../commons/utils/Utils';
import { StyledTableCellProps as StyledTableCell, StyledTooltip } from '../../commons/utils/TableStyles';
import LocaleFormat from '../../commons/utils/LocaleFormat.js';

/**
 * VEC-3312 Scenario 05 — alerta por stock debajo del mínimo.
 *
 * Solo alerta cuando hay un mínimo configurado mayor a cero: stockMinimo NULL (ítem sin
 * mínimo definido) o 0 significa "no controlar", así que no debe pintar nada. El ítem sigue
 * operable con normalidad en cualquier caso: la alerta es puramente visual.
 */
const estaBajoMinimo = (row) => {
  const minimo = row.stockMinimo;
  const actual = row.stockActual;
  if (minimo === null || minimo === undefined || Number(minimo) <= 0) return false;
  if (actual === null || actual === undefined) return false;
  return Number(actual) < Number(minimo);
};

/**
 * La descripción del ítem es texto libre cargado por el usuario (ABM, importación masiva, ABM
 * legacy de Pañol2 e integraciones), y el modal de baja la inyecta con la opción `html` de
 * SweetAlert2, que interpreta HTML. Sin escapar sería XSS almacenado: el payload lo carga quien
 * puede editar ítems y lo ejecuta cualquier otro usuario al abrir la confirmación.
 *
 * La grilla no necesita esto: React escapa solo lo que se renderiza como JSX.
 */
const escapeHtml = (s) =>
  String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const InventarioItemsGridRow = (props) => {
  const intl = props.intl;
  const row = props.row;
  const baseUrl = props.baseUrl;
  const [redirectTo, setRedirectTo] = useState(null);

  const bajoMinimo = estaBajoMinimo(row);

  const formatDecimal = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return LocaleFormat.formatNumber(Number(value), 2);
  };

  const handleDesactivar = () => {
    Swal.fire({
      title: intl.formatMessage({
        id: 'inventarioItems.desactivar.confirma',
        defaultMessage: '¿Confirma la desactivación?',
      }),
      html:
        intl.formatMessage({
          id: 'inventarioItems.desactivar.se_desactivara_el_item',
          defaultMessage: 'Se desactivará el ítem',
        }) +
        ': <b>' +
        escapeHtml(row.descripcion || '') +
        '</b><br/>' +
        intl.formatMessage({
          id: 'inventarioItems.desactivar.no_podra_seleccionarse',
          defaultMessage:
            'No podrá seleccionarse para nuevos movimientos de inventario ni para consumo en Órdenes de Trabajo. Los movimientos históricos no se modifican.',
        }),
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: intl.formatMessage({ id: 'yes', defaultMessage: 'Si' }),
      cancelButtonText: intl.formatMessage({ id: 'no', defaultMessage: 'No' }),
    }).then((result) => {
      if (result.value) {
        Utils.sendData('/inventario/items/' + row.id + '/desactivar', 'PUT', {})
          .then(() => {
            props.fetchRows();
            Swal(
              intl.formatMessage({ id: 'inventarioItems.desactivar.desactivado', defaultMessage: 'Desactivado!' }),
              '',
              'success'
            );
          })
          .catch((err) => {
            Swal(err && err.getMessage ? err.getMessage() : 'Error', '', 'error');
          });
      }
    });
  };

  return (
    <>
      {redirectTo && <Redirect push to={redirectTo} />}
      <TableRow>
        <StyledTableCell align="left">{row.numeroDeParte || '-'}</StyledTableCell>
        {/*
          Estado activo/inactivo como franja lateral de color, que es como lo muestran las grillas
          del resto de la app (RepuestosGridRow, MotivosGridRow y otras 30). Antes se atenuaba la
          fila entera con opacity: se leía como "deshabilitado" y no como un estado, y no coincidía
          con nada del sistema.
        */}
        <td
          align="left"
          className={(row.activo ? 'status-green' : 'status-red') + ' p-0 all custom-lineHeight br'}
          style={{ maxWidth: '3px' }}
          title={
            row.activo
              ? intl.formatMessage({ id: 'inventarioItems.grid.estado_activo', defaultMessage: 'Activo' })
              : intl.formatMessage({ id: 'inventarioItems.grid.estado_inactivo', defaultMessage: 'Inactivo' })
          }
        />
        <StyledTableCell align="left">{row.externalCode || '-'}</StyledTableCell>
        <StyledTableCell align="left">{row.descripcion}</StyledTableCell>
        <StyledTableCell align="left">{row.categoriaNombre || '-'}</StyledTableCell>
        <StyledTableCell align="left">{row.subcategoriaNombre || '-'}</StyledTableCell>
        <StyledTableCell align="left">{row.unidadNombre || '-'}</StyledTableCell>
        <StyledTableCell align="left">{row.marcaNombre || '-'}</StyledTableCell>
        <StyledTableCell align="left">{row.modelo || '-'}</StyledTableCell>
        <StyledTableCell align="right">{formatDecimal(row.costo)}</StyledTableCell>
        <StyledTableCell align="right">{formatDecimal(row.stockMinimo)}</StyledTableCell>
        <StyledTableCell align="right">
          {bajoMinimo ? (
            <StyledTooltip
              title={intl.formatMessage(
                {
                  id: 'inventarioItems.grid.alerta_bajo_minimo',
                  defaultMessage: 'Stock por debajo del mínimo ({minimo})',
                },
                { minimo: formatDecimal(row.stockMinimo) }
              )}
              arrow
              placement="left"
            >
              <span className="text-danger" style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                <i className="fa fa-exclamation-triangle mr-1" />
                {formatDecimal(row.stockActual)}
              </span>
            </StyledTooltip>
          ) : (
            formatDecimal(row.stockActual)
          )}
        </StyledTableCell>
        <StyledTableCell>
          <div className="d-inline-flex">
            {props.permissions.visualizar && (
              <button
                className="action view btn btn-sm btn-icon btn-dt-grid text-success"
                title={intl.formatMessage({ id: 'Ver', defaultMessage: 'Ver' })}
                data-toggle="tooltip"
                data-placement="top"
                onClick={() => setRedirectTo(baseUrl + '/' + row.id)}
              >
                <i className="fa fa-search fa-xs" />
              </button>
            )}
            {props.permissions.modificar && (
              <button
                className="action edit btn btn-sm btn-icon btn-dt-grid text-success"
                title={intl.formatMessage({ id: 'Editar', defaultMessage: 'Editar' })}
                data-toggle="tooltip"
                data-placement="top"
                onClick={() => setRedirectTo(baseUrl + '/' + row.id + '/edit')}
              >
                <i className="fa fa-pencil fa-xs" />
              </button>
            )}
            {/*
              VEC-3314 — acceso rápido a cargar stock, con el ítem ya seleccionado.

              Solo para ítems ACTIVOS: el backend rechaza con 400 el ingreso a un ítem dado de baja
              (sería revivirlo sin pasar por el ABM), así que mostrar el botón ahí sería ofrecer un
              camino que termina en error.

              El ítem viaja por query string y no por la ruta porque la pantalla de ingreso también
              se puede abrir sin ítem: es una preselección, no parte de su identidad.
            */}
            {props.permissions.ingresarStock && row.activo ? (
              <button
                className="action btn btn-sm btn-icon btn-dt-grid text-success"
                title={intl.formatMessage({
                  id: 'inventarioItems.grid.cargar_stock',
                  defaultMessage: 'Cargar stock',
                })}
                data-toggle="tooltip"
                data-placement="top"
                onClick={() => setRedirectTo('/inventario/stock/ingresos/nuevo?item=' + row.id)}
              >
                <i className="fa fa-plus fa-xs" />
              </button>
            ) : (
              ''
            )}
            {props.permissions.desactivar && row.activo ? (
              <button
                className="action delete btn btn-sm btn-icon btn-dt-grid text-danger"
                title={intl.formatMessage({ id: 'Desactivar', defaultMessage: 'Desactivar' })}
                data-toggle="tooltip"
                data-placement="top"
                onClick={() => handleDesactivar()}
              >
                <i className="fa fa-trash fa-xs" />
              </button>
            ) : (
              ''
            )}
          </div>
        </StyledTableCell>
      </TableRow>
    </>
  );
};

export default InventarioItemsGridRow;
